import { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const LANGS = [
  { code: "NL", label: "NL" },
  { code: "FR", label: "FR" },
  { code: "EN", label: "EN" },
];

export default function Navbar() {
  const { t, language, setLanguage } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      setMobileOpen(false);
    }
  };

  const navLinks = [
    { label: t.nav.home, id: "hero" },
    { label: t.nav.about, id: "about" },
    { label: t.nav.services, id: "services" },
    { label: t.nav.pricing, id: "pricing" },
    { label: t.nav.download, id: "download" },
    { label: t.nav.contact, id: "contact" },
  ];

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2 group"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <div className="w-8 h-8 bg-red-600 flex items-center justify-center">
              <span className="text-white font-black text-sm font-heading">R</span>
            </div>
            <span className="text-lg font-black tracking-tight text-gray-900 font-heading">
              RENT<span className="text-red-600">COMING</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-link-${link.id}`}
                onClick={() => scrollTo(link.id)}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200 font-body tracking-wide uppercase"
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="language-switcher"
                  className="rounded-none text-sm font-medium font-body gap-1"
                >
                  {language}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-none min-w-[80px]">
                {LANGS.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`lang-option-${lang.code}`}
                    onClick={() => setLanguage(lang.code)}
                    className="rounded-none cursor-pointer font-body"
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              data-testid="nav-cta-btn"
              onClick={() => scrollTo("download")}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-semibold text-sm px-6 font-body"
            >
              {t.nav.cta}
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-gray-200"
        >
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`mobile-nav-link-${link.id}`}
                onClick={() => scrollTo(link.id)}
                className="block w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 py-2 font-body uppercase tracking-wide"
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-3 border-t border-gray-200">
              {LANGS.map((lang) => (
                <button
                  key={lang.code}
                  data-testid={`mobile-lang-${lang.code}`}
                  onClick={() => {
                    setLanguage(lang.code);
                    setMobileOpen(false);
                  }}
                  className={`text-sm font-medium px-3 py-1 font-body ${
                    language === lang.code
                      ? "bg-red-600 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <Button
              data-testid="mobile-cta-btn"
              onClick={() => scrollTo("download")}
              className="w-full rounded-none bg-red-600 hover:bg-red-700 text-white font-semibold text-sm font-body"
            >
              {t.nav.cta}
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
